import React from 'react'
import { VerseToDisplayProps } from "../../props/versetodisplayprops";
import BigButtonWithTextAtRight from '../bigButton/BigButtonWithTextAtRight'
import { HcContext, SizeOfWindow } from "../../types/hccontext";


interface ReadingContentProps {
    language: string;
    type: string;
    content: VerseToDisplayProps[];
    allowToDisplayLinkFromCorpus: boolean;
    hcContext: HcContext;
    selectedVerses?: number[];
}

function ReadingContent({ language, type, content, allowToDisplayLinkFromCorpus, hcContext, selectedVerses }: Readonly<ReadingContentProps>) {

    const zeroPad = (num: number, places: number) => String(num).padStart(places, '0');
    const firstHighlightedRef = React.useRef<HTMLDivElement | null>(null);

    const hasScrolled = React.useRef(false);

    const setFirstHighlightedRef = (el: HTMLDivElement | null) => {
        if (el && !hasScrolled.current) {
            hasScrolled.current = true;
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    function PrintVerse({ nb, text }: Readonly<{ nb: number; text: string }>) {
        return (
            <>
                <span style={{ color: "#bf2329" }}>{zeroPad(nb, 2)}</span>{" "}
                <span dangerouslySetInnerHTML={{ __html: text }} />
            </>
        )
    }

    // Group consecutive verses into blocks (highlighted or not)
    function getVerseBlocks(): { isHighlighted: boolean, verses: VerseToDisplayProps[], firstVerseNumber: number }[] {
        const blocks: { isHighlighted: boolean, verses: VerseToDisplayProps[], firstVerseNumber: number }[] = [];
        if (!content) return blocks;

        let currentBlock: { isHighlighted: boolean, verses: VerseToDisplayProps[] } | null = null;

        for (const item of content) {
            const isHighlighted = selectedVerses?.includes(Number(item.nb)) ?? false;

            if (!currentBlock || currentBlock.isHighlighted !== isHighlighted) {
                if (currentBlock) {
                    blocks.push({
                        ...currentBlock,
                        firstVerseNumber: Number(currentBlock.verses[0].nb)
                    });
                }
                currentBlock = { isHighlighted, verses: [item] };
            } else {
                currentBlock.verses.push(item);
            }
        }
        if (currentBlock) {
            blocks.push({
                ...currentBlock,
                firstVerseNumber: Number(currentBlock.verses[0].nb)
            });
        }

        return blocks;
    }

    const highlightStyle = {
        border: "2px solid #ffa500",
        borderRadius: 8,
        padding: "8px 12px",
        marginBottom: 16,
        backgroundColor: hcContext.getBotBubbleBackgroundColor(),
        scrollMarginTop: "100px",  // ← offset if you have a sticky top bar
    };

    return (
        <React.Fragment>

            {/* === Long Screen === */}
            <div className='hc-long-screen'>
                {getVerseBlocks().map((block, blockIndex) => {
                    const isFirstHighlighted =
                        block.isHighlighted &&
                        selectedVerses?.length &&
                        Number(block.verses[0].nb) === Math.min(...selectedVerses);

                    return (
                        <>
                            {block.isHighlighted ? (
                                // Wrap whole table inside highlight box
                                <div style={highlightStyle}
                                    ref={(selectedVerses?.length &&
                                        Number(block.verses[0].nb) === Math.min(...selectedVerses) &&
                                        hcContext.sizeOfWindow !== SizeOfWindow.VERY_SHORT)
                                        ? setFirstHighlightedRef
                                        : undefined}
                                >
                                    <table key={`long-block-${blockIndex}`} style={{ width: '100%' }}>
                                        <thead className="visually-hidden"><tr><th></th></tr></thead>
                                        <tbody>
                                            {block.verses.map((item, index) => {
                                                const showEllipsis = index > 0 && (Number(block.verses[index - 1].nb) + 1) < Number(item.nb);

                                                return (
                                                    <tr key={`verse-row-${item.nb}-${index}`}>
                                                        {/* Verse column */}
                                                        <td style={{ width: '50%', paddingLeft: 5, paddingRight: 15, verticalAlign: "top" }}>
                                                            <div className='hc-content-text' style={{ marginLeft: 10 }}>
                                                                {showEllipsis && <><div style={{ marginLeft: 5 }}>[...]</div><br /></>}
                                                                <PrintVerse nb={item.nb} text={item.text} />
                                                                <br /><br />
                                                            </div>
                                                        </td>

                                                        {/* Button column */}
                                                        <td style={{ width: '50%', paddingLeft: 15, paddingRight: 5, verticalAlign: "top" }}>
                                                            {item.link &&
                                                                <div style={{ marginLeft: 30 }}>
                                                                    <BigButtonWithTextAtRight
                                                                        allowPreview={true}
                                                                        allowToDisplayLinkFromCorpus={allowToDisplayLinkFromCorpus}
                                                                        language={language}
                                                                        item={item.link}
                                                                        hcContext={hcContext}
                                                                    />
                                                                    <br />
                                                                </div>
                                                            }
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                // Non-highlighted blocks (no wrapping needed)
                                <table key={`long-block-${blockIndex}`} style={{ width: '100%' }}>
                                    <thead className="visually-hidden"><tr><th></th></tr></thead>
                                    <tbody>
                                        {block.verses.map((item, index) => {
                                            const showEllipsis = index > 0 && (Number(block.verses[index - 1].nb) + 1) < Number(item.nb);

                                            return (
                                                <tr key={`verse-row-${item.nb}-${index}`}>
                                                    {/* Verse column */}
                                                    <td style={{ width: '50%', paddingLeft: 5, paddingRight: 15, verticalAlign: "top" }}>
                                                        <div className='hc-content-text' style={{ marginLeft: 10 }}>
                                                            {showEllipsis && <><div style={{ marginLeft: 5 }}>[...]</div><br /></>}
                                                            <PrintVerse nb={item.nb} text={item.text} />
                                                            <br /><br />
                                                        </div>
                                                    </td>

                                                    {/* Button column */}
                                                    <td style={{ width: '50%', paddingLeft: 15, paddingRight: 5, verticalAlign: "top" }}>
                                                        {item.link &&
                                                            <div style={{ marginLeft: 30 }}>
                                                                <BigButtonWithTextAtRight
                                                                    allowPreview={true}
                                                                    allowToDisplayLinkFromCorpus={allowToDisplayLinkFromCorpus}
                                                                    language={language}
                                                                    item={item.link}
                                                                    hcContext={hcContext}
                                                                />
                                                                <br />
                                                            </div>
                                                        }
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </>


                    );
                })}
            </div>

            {/* === Short Screen === */}
            <div className='hc-short-screen'>
                {getVerseBlocks().map((block, blockIndex) => {
                    const isFirstHighlighted = block.isHighlighted && selectedVerses?.length && Number(block.verses[0].nb) === Math.min(...selectedVerses);
                    return (
                        <div
                            key={`short-block-${blockIndex}`}
                            ref={(isFirstHighlighted && hcContext.sizeOfWindow === SizeOfWindow.VERY_SHORT) ? setFirstHighlightedRef : undefined}
                            style={block.isHighlighted ? highlightStyle : undefined}
                        >
                            {block.verses.map((item, index) => (
                                <React.Fragment key={`short-${item.nb}-${index}`}>
                                    <div className='hc-content-text'>
                                        <div style={{ marginRight: 20 }}>
                                            <PrintVerse nb={item.nb} text={item.text} />
                                        </div>
                                        <br />
                                    </div>
                                    {item.link &&
                                        <div style={{ marginRight: 20 }}>
                                            <BigButtonWithTextAtRight allowPreview={true} language={language} item={item.link} allowToDisplayLinkFromCorpus={allowToDisplayLinkFromCorpus} hcContext={hcContext} />
                                            <br />
                                        </div>
                                    }
                                </React.Fragment>
                            ))}
                        </div>
                    );
                })}
            </div>

        </React.Fragment>
    );
}

export default ReadingContent;